import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { PatrolAbsorber, UpdatePatrolSystemAbsorberBody } from "../../../../../http/patrol/types/absorbers";
import { useAbsorbersApi } from "../../../../../http/patrol/useAbsorbers";
import { usePatrolSteelVersionsApi } from "../../../../../http/patrol/useSteelVersion";
import { AdminCommonSteelVersion } from "../../../../../http/types/commonSteelVersions";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import styles from "./SystemAbsorberForm.module.css";

interface SystemAbsorberFormBody {
  item: SearchAdminItem | null;
  material: AdminCommonSteelVersion | null;
  minSpan: number | null;
  maxSpanCurve: number | null;
  maxSpanIntermediate: number | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  maxUsers: number | null;
}

interface SystemAbsorberFormProps {
  defaultValues?: PatrolAbsorber;
  onClose: () => void;
}

const SystemAbsorberForm: FC<SystemAbsorberFormProps> = ({ onClose, defaultValues }) => {
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const itemsApi = useItemsApi();
  const absorbersApi = useAbsorbersApi();
  const supportMaterialApi = usePatrolSteelVersionsApi();

  const materials = supportMaterialApi.useAdminMaterials({}, aborter.current.signal);

  if (!patrolSystemId) {
    return;
  }

  return (
    <Form<SystemAbsorberFormBody>
      defaultValues={{ item: null, order: 50, visibility: "hidden" }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: onClose }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        const common: UpdatePatrolSystemAbsorberBody = {
          maxSpanCurve: formData.maxSpanCurve ?? 0,
          maxSpanIntermediate: formData.maxSpanIntermediate ?? 0,
          minSpan: formData.minSpan ?? 0,
          maxUsers: formData.maxUsers ?? undefined,
        };
        if (defaultValues?.item.id)
          try {
            await absorbersApi.updateAdminSystemAbsorber(patrolSystemId, defaultValues.item.id, common);
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        else
          try {
            await absorbersApi.insertAdminSystemAbsorber(patrolSystemId, {
              ...common,
              itemId: formData.item?.id ?? "",
              materialId: formData.material?.id ?? "",
              order: formData.order!,
              visibility: formData.visibility,
            });
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
      }}
    >
      {({ control, setValue, watch, formState: { errors }, reset }) => {
        useEffect(() => {
          defaultValues &&
            reset({
              item: defaultValues.item,
              material: defaultValues.material,
              order: defaultValues.order,
              visibility: defaultValues.visibility,
              maxSpanCurve: defaultValues.systems[0]?.maxSpanCurve,
              minSpan: defaultValues.systems[0]?.minSpan,
              maxSpanIntermediate: defaultValues.systems[0]?.maxSpanIntermediate,
              maxUsers: defaultValues.systems[0]?.maxUsers,
            });
        }, [defaultValues?.item.id]);

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isClearable
                            getOptionLabel={(x) => x.code}
                            isDisabled={!!defaultValues?.item.id}
                            getOptionValue={(x) => x.id}
                            onChange={(value) => setValue("item", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="material"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.materials.singular}
                  error={fieldState.error}
                  warning={
                    defaultValues?.material.id != field.value?.id
                      ? i18n.translation.common.warningThisEditCouldAffectOtherSystemsAsWell
                      : undefined
                  }
                >
                  {({ labelId }) => (
                    <CustomSelect<AdminCommonSteelVersion>
                      {...field}
                      inputId={labelId}
                      options={materials.data?.items ?? []}
                      isDisabled={!!materials.error || isSubmitting}
                      isLoading={materials.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.spans}>
              {({ labelId }) => (
                <>
                  <div className={styles.inline}>
                    <span>{i18n.translation.common.minSpanBetweenSupports}:</span>
                    <Controller
                      name="minSpan"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => (
                        <Numeric
                          {...field}
                          id={labelId + "_minSpan"}
                          disabled={isSubmitting}
                          isInvalid={!!errors.minSpan}
                          decimalScale={1}
                          allowNegative={false}
                          suffix=" mt"
                          onBlur={(value) => setValue("minSpan", value)}
                        />
                      )}
                    />
                  </div>
                  {errors.minSpan && (
                    <FormField.ErrorMessage
                      message={errors.minSpan.message}
                      type={errors.minSpan.type}
                      root={errors.minSpan.root}
                      types={errors.minSpan.types}
                    />
                  )}
                  <div className={styles.inline}>
                    <span>{i18n.translation.common.maxSpanBetweenCurveAndIntermediate}:</span>
                    <Controller
                      name="maxSpanCurve"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => (
                        <Numeric
                          {...field}
                          id={labelId + "_width"}
                          disabled={isSubmitting}
                          isInvalid={!!errors.maxSpanCurve}
                          decimalScale={1}
                          allowNegative={false}
                          suffix=" mt"
                          onBlur={(value) => setValue("maxSpanCurve", value)}
                        />
                      )}
                    />
                  </div>
                  {errors.maxSpanCurve && (
                    <FormField.ErrorMessage
                      message={errors.maxSpanCurve.message}
                      type={errors.maxSpanCurve.type}
                      root={errors.maxSpanCurve.root}
                      types={errors.maxSpanCurve.types}
                    />
                  )}
                  <div className={styles.inline}>
                    <span>{i18n.translation.common.maxSpanBetweenIntermediate}:</span>
                    <Controller
                      name="maxSpanIntermediate"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => (
                        <Numeric
                          {...field}
                          id={labelId + "_width"}
                          disabled={isSubmitting}
                          isInvalid={!!errors.maxSpanIntermediate}
                          decimalScale={1}
                          allowNegative={false}
                          suffix=" mt"
                          onBlur={(value) => setValue("maxSpanIntermediate", value)}
                        />
                      )}
                    />
                  </div>
                  {errors.maxSpanIntermediate && (
                    <FormField.ErrorMessage
                      message={errors.maxSpanIntermediate.message}
                      type={errors.maxSpanIntermediate.type}
                      root={errors.maxSpanIntermediate.root}
                      types={errors.maxSpanIntermediate.types}
                    />
                  )}
                </>
              )}
            </FormField>
            <Controller
              name="maxUsers"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.maxUsers} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("maxUsers", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.common.visibility}
                  warning={
                    defaultValues?.visibility != watch("visibility")
                      ? i18n.translation.common.warningThisEditCouldAffectOtherSystemsAsWell
                      : undefined
                  }
                  error={fieldState.error}
                  isOptional
                >
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.common.order}
                  error={fieldState.error}
                  warning={
                    defaultValues?.order !== field.value
                      ? i18n.translation.common.warningThisEditCouldAffectOtherSystemsAsWell
                      : undefined
                  }
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      disabled={isSubmitting}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SystemAbsorberForm;
